import { useEffect } from 'react';

import { usePatientKeypair } from '../polling-feature/patient-keypair-context.js';
import { usePollStatus } from '../polling-feature/use-poll-status.js';
import type { CancelMachine } from './use-cancel-machine.js';
import type { RecoveryState } from '../recovery-feature/use-recovery-bootstrap.js';
import type { PinningResult } from '../../pinning-module/verify-pinning.js';

/**
 * Reagiert auf den Recovery-Bootstrap. `recovered` triggert einen einmaligen
 * `GOTO_LOADING`, sodass der Polling-Hook übernimmt. `no-recovery` und
 * `expired` führen zum frischen Start-Flow ohne weiteren Dispatch.
 */
export function useRecoveryAutoDispatch(recovery: RecoveryState, machine: CancelMachine): void {
  const { dispatch } = machine;
  const startScreen = machine.state.screen === 'START';
  useEffect(() => {
    if (recovery.status !== 'recovered' || !startScreen) {
      return;
    }
    dispatch({ type: 'GOTO_LOADING' });
  }, [recovery.status, startScreen, dispatch]);
}

/**
 * Verdrahtet den Polling-Hook gegen den Keypair-Context. Der Hook läuft nur,
 * wenn `LOADING` aktiv und ein Keypair-Eintrag im Context steht — sonst ist
 * `enabled === false` und der Hook ist still.
 */
export function usePollingForLoadingScreen(machine: CancelMachine): void {
  const { entry } = usePatientKeypair();
  const isLoading = machine.state.screen === 'LOADING';
  usePollStatus({
    enabled: isLoading && entry !== null,
    dispatch: machine.dispatch,
    ...(entry !== null
      ? {
          tenantId: entry.tenantId,
          queueItemId: entry.queueItemId,
          privateKey: entry.privateKey,
        }
      : {}),
  });
}

/**
 * Reagiert auf einen Pinning-Fehler einmalig pro Result-Identität: dispatched
 * `PINNING_FAILED` mit dem Sub-Code.
 */
export function usePinningErrorDispatch(
  result: PinningResult | null,
  machine: CancelMachine,
): void {
  const { dispatch } = machine;
  useEffect(() => {
    if (result === null || result.ok) {
      return;
    }
    dispatch({ type: 'PINNING_FAILED', pinningError: result.reason });
  }, [result, dispatch]);
}
